import { z } from 'zod'

export type OrganizationV1 = {
  _id: string
  id: string
  customerId: string
  name: string
  locationIds: string[]
  contactIds: never[]
  legalEntityIds: string[]
  isMain: boolean
}

const AccountDetailsSchema = z.object({
  accountNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
  binNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
})

export type AccountDetails = z.infer<typeof AccountDetailsSchema>

export const CustomsSettingsSchema = z.object({
  customsDefermentAccount: AccountDetailsSchema.nullish().transform(
    (data) =>
      data ?? {
        accountNumber: '',
        binNumber: undefined,
      }
  ),
  vatAccount: AccountDetailsSchema.nullish().transform(
    (data) =>
      data ?? {
        accountNumber: '',
        binNumber: undefined,
      }
  ),
})

export type CustomsSettings = z.infer<typeof CustomsSettingsSchema>

export const OrganizationSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  name: z.string(),
  isMain: z
    .boolean()
    .nullish()
    .transform((data) => data ?? false),
  legalEntityIds: z.array(z.string()),
  contactIds: z.array(z.string()),
  locationIds: z.array(z.string()),
  customsSettings: CustomsSettingsSchema.nullish().transform(
    (data) =>
      data ?? {
        customsDefermentAccount: {
          accountNumber: '',
          binNumber: '',
        },
        vatAccount: {
          accountNumber: '',
          binNumber: '',
        },
      }
  ),
})

export const OrganizationsSchema = z.object({
  data: z.array(OrganizationSchema),
  total: z.number(),
})

export const BINNumberSchema = z
  .union([
    z.object({
      customsDefermentAccount: z.object({
        binNumber: z.string(),
      }),
    }),
    z.object({
      vatAccount: z.object({
        binNumber: z.string(),
      }),
    }),
  ])
  .transform((data) => {
    if ('customsDefermentAccount' in data) {
      return {
        binNumber: data.customsDefermentAccount.binNumber,
      }
    } else if ('vatAccount' in data) {
      return {
        binNumber: data.vatAccount.binNumber,
      }
    }
    return { binNumber: '' }
  })

export type Organization = z.infer<typeof OrganizationSchema>
export type Organizations = z.infer<typeof OrganizationsSchema>
